import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Company {
    id: number;
    name: string;
    address?: string;
    city?: string;
    phone?: string;
    email?: string;
    logoPath?: string;
    isActive?: boolean;
    ntn?: string;
    strn?: string;
    signaturePath?: string;
    stampPath?: string;
    hasSignature?: boolean;
    showDnHeader?: boolean;
}

export interface FuelDispatch {
  id: number;
  companyId: number;
  sr?: number;
  toLocation?: string;
  product?: string;
  fromLocation?: string;
  address?: string;
  dnNumber?: string;
  indentNumber?: string;
  tlNumber?: string;
  documentDate?: string;
  carriageContractor?: string;
  quantity?: number;
  ambiantLiters?: number;
  metricTons?: number;
  tank1RD?: number; tank2RD?: number; tank3RD?: number; tank4RD?: number; tank5RD?: number;
  tank1PD?: number; tank2PD?: number; tank3PD?: number; tank4PD?: number; tank5PD?: number;
  tank1QD?: number; tank2QD?: number; tank3QD?: number; tank4QD?: number; tank5QD?: number;
}

export interface PagedResult {
  total: number;
  page: number;
  pageSize: number;
  data: FuelDispatch[];
}

export interface AppUser {
    id: number;
    username: string;
    password?: string;
    fullName?: string;
    isActive?: boolean;
    createdAt?: string;
}

export interface SalesRegister {
    id: number;
    companyId: number;
    sr?: number;
    customerName?: string;
    product?: string;
    documentNumber?: string;
    fromLocation?: string;
    customerCode?: string;
    address?: string;
    ntn?: string;
    tlNumber?: string;
    documentDate?: string;
    fn?: string;
    quantityLiters?: number;
    unitPrice?: number;
    valueOfSalesExclTax?: number;
    salesTax18?: number;
    extraMargin?: number;
    franchiseFee?: number;
    fed?: number;
    dealerCommission?: number;
    withholdingOnDealerCommission?: number;
    totalReceivable?: number;
    netSales?: number;
    region?: string;
}

export interface SalesPagedResult {
    total: number;
    page: number;
    pageSize: number;
    data: SalesRegister[];
}

export interface Supplier {
    id: number;
    companyId: number;
    name: string;
    address?: string;
    ntn?: string;
    strn?: string;
    isActive?: boolean;
}

export interface DeliveryNote {
    id: number;
    companyId: number;
    sr?: number;
    fromLocation?: string;
    toLocation?: string;
    documentDate?: string;
    dnNumber?: string;
    tankLorryNo?: string;
    indentNumber?: string;
    quantity?: number;
    calibrationNo?: string;
    calibrationExpiry?: string;
    carriageContractor?: string;
    deliveryAddress?: string;
    quantityCh1?: number;
    quantityCh2?: number;
    quantityCh3?: number;
    quantityCh4?: number;
    quantityCh5?: number;
    productCh1?: string;
    productCh2?: string;
    productCh3?: string;
    productCh4?: string;
    productCh5?: string;
    loadRefDipCh1?: number;
    loadRefDipCh2?: number;
    loadRefDipCh3?: number;
    loadRefDipCh4?: number;
    loadRefDipCh5?: number;
    loadProdDipCh1?: number;
    loadProdDipCh2?: number;
    loadProdDipCh3?: number;
    loadProdDipCh4?: number;
    loadProdDipCh5?: number;
    loadTempCh1?: number;
    loadTempCh2?: number;
    loadTempCh3?: number;
    loadTempCh4?: number;
    loadTempCh5?: number;
    loadDensityCh1?: number;
    loadDensityCh2?: number;
    loadDensityCh3?: number;
    loadDensityCh4?: number;
    loadDensityCh5?: number;
    recvRefDipCh1?: number;
    recvRefDipCh2?: number;
    recvRefDipCh3?: number;
    recvRefDipCh4?: number;
    recvRefDipCh5?: number;
    recvProdDipCh1?: number;
    recvProdDipCh2?: number;
    recvProdDipCh3?: number;
    recvProdDipCh4?: number;
    recvProdDipCh5?: number;
    recvTempCh1?: number;
    recvTempCh2?: number;
    recvTempCh3?: number;
    recvTempCh4?: number;
    recvTempCh5?: number;
    recvDensityCh1?: number;
    recvDensityCh2?: number;
    recvDensityCh3?: number;
    recvDensityCh4?: number;
    recvDensityCh5?: number;
    shortInMMCh1?: number;
    shortInMMCh2?: number;
    shortInMMCh3?: number;
    shortInMMCh4?: number;
    shortInMMCh5?: number;
    shortInLtrsCh1?: number;
    shortInLtrsCh2?: number;
    shortInLtrsCh3?: number;
    shortInLtrsCh4?: number;
    shortInLtrsCh5?: number;
    sealNumber?: string;
    sealTotal?: string;
    cumulShortAmbLtrs?: number;
    cumulShortStdLtrs?: number;
    cumulShortMTons?: number;
    netRecvAmbLtrs?: number;
    netRecvStdLtrs?: number;
    netRecvMTons?: number;
    totalAmbientLitters?: number;
    metricTons?: number;
    authorizedSignature?: string;
}

export interface DeliveryNotePagedResult {
    total: number;
    page: number;
    pageSize: number;
    data: DeliveryNote[];
}

@Injectable({ providedIn: 'root' })
export class ApiService {
    private base = environment.apiUrl;

    constructor(private http: HttpClient) {}

    // sirf active (dropdown)
    getCompanies(): Observable<Company[]> {
        return this.http.get<Company[]>(`${this.base}/companies`);
    }

    // sab (active + inactive) - management page
    getAllCompanies(): Observable<Company[]> {
        return this.http.get<Company[]>(`${this.base}/companies?all=true`);
    }

    // activate
    activateCompany(id: number): Observable<any> {
        return this.http.put(`${this.base}/companies/${id}/activate`, {});
    }

    uploadExcel(companyId: number, file: File): Observable<any> {
        const form = new FormData();
        form.append('file', file);
        return this.http.post(`${this.base}/fueldispatch/upload?companyId=${companyId}`, form);
    }

    getList(companyId: number | null, page: number, pageSize: number, search: string): Observable<PagedResult> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        if (companyId) params = params.set('companyId', companyId);
        if (search) params = params.set('search', search);
        return this.http.get<PagedResult>(`${this.base}/fueldispatch`, { params });
    }

    update(id: number, dto: FuelDispatch): Observable<any> {
        return this.http.put(`${this.base}/fueldispatch/${id}`, dto);
    }

    delete(id: number): Observable<any> {
        return this.http.delete(`${this.base}/fueldispatch/${id}`);
    }

    // ===================================================================
    // YEH 2 METHODS ApiService CLASS KE ANDAR ADD KAREIN
    // (delete() method ke baad)
    // ===================================================================

    // current filter/search ke saare IDs (Select All Records)
    getAllIds(companyId: number | null, search: string): Observable<number[]> {
        let params = new HttpParams();
        if (companyId) params = params.set('companyId', companyId);
        if (search) params = params.set('search', search);
        return this.http.get<number[]>(`${this.base}/fueldispatch/ids`, { params });
    }

    // bulk delete
    bulkDelete(ids: number[]): Observable<any> {
        return this.http.post(`${this.base}/fueldispatch/bulk-delete`, ids);
    }

    // ===================================================================
    // YEH METHODS ApiService CLASS KE ANDAR ADD KAREIN
    // (getCompanies() method ke baad)
    // ===================================================================

    // ek company (edit ke liye)
    getCompany(id: number): Observable<Company> {
        return this.http.get<Company>(`${this.base}/companies/${id}`);
    }

    // create
    createCompany(c: Company): Observable<any> {
        return this.http.post(`${this.base}/companies`, c);
    }

    // update
    updateCompany(id: number, c: Company): Observable<any> {
        return this.http.put(`${this.base}/companies/${id}`, c);
    }

    // delete
    deleteCompany(id: number): Observable<any> {
        return this.http.delete(`${this.base}/companies/${id}`);
    }

    // logo upload
    uploadLogo(id: number, file: File): Observable<any> {
        const form = new FormData();
        form.append('file', file);
        return this.http.post(`${this.base}/companies/${id}/logo`, form);
    }

    uploadSignature(id: number, file: File): Observable<any> {
        const form = new FormData();
        form.append('file', file);
        return this.http.post(`${this.base}/companies/${id}/signature`, form);
    }

    uploadStamp(id: number, file: File): Observable<any> {
        const form = new FormData();
        form.append('file', file);
        return this.http.post(`${this.base}/companies/${id}/stamp`, form);
    }

    // logo remove
    removeLogo(id: number): Observable<any> {
        return this.http.delete(`${this.base}/companies/${id}/logo`);
    }
    removeSignature(id: number): Observable<any> {
        return this.http.delete(`${this.base}/companies/${id}/signature`);
    }
    removeStamp(id: number): Observable<any> {
        return this.http.delete(`${this.base}/companies/${id}/stamp`);
    }

    getUsers(): Observable<AppUser[]> {
        return this.http.get<AppUser[]>(`${this.base}/users`);
    }

    createUser(u: AppUser): Observable<any> {
        return this.http.post(`${this.base}/users`, u);
    }

    updateUser(id: number, u: AppUser): Observable<any> {
        return this.http.put(`${this.base}/users/${id}`, u);
    }

    deleteUser(id: number): Observable<any> {
        return this.http.delete(`${this.base}/users/${id}`);
    }

    activateUser(id: number): Observable<any> {
        return this.http.put(`${this.base}/users/${id}/activate`, {});
    }

    // ---- Sales Register ----
    uploadSales(companyId: number, file: File): Observable<any> {
        const form = new FormData();
        form.append('file', file);
        return this.http.post(`${this.base}/salesregister/upload?companyId=${companyId}`, form);
    }

    // getSalesList(companyId: number | null, page: number, pageSize: number, search: string): Observable<SalesPagedResult> {
    //     let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    //     if (companyId) params = params.set('companyId', companyId);
    //     if (search) params = params.set('search', search);
    //     return this.http.get<SalesPagedResult>(`${this.base}/salesregister`, { params });
    // }

    getSalesList(companyId: number | null, page: number, pageSize: number, search: string, fromDate?: string | null, toDate?: string | null, region?: string | null): Observable<SalesPagedResult> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        if (companyId) params = params.set('companyId', companyId);
        if (search) params = params.set('search', search);
        if (fromDate) params = params.set('fromDate', fromDate);
        if (toDate) params = params.set('toDate', toDate);
        if (region) params = params.set('region', region);
        return this.http.get<SalesPagedResult>(`${this.base}/salesregister`, { params });
    }

    // getSalesIds(companyId: number | null, search: string): Observable<number[]> {
    //     let params = new HttpParams();
    //     if (companyId) params = params.set('companyId', companyId);
    //     if (search) params = params.set('search', search);
    //     return this.http.get<number[]>(`${this.base}/salesregister/ids`, { params });
    // }

    getSalesIds(companyId: number | null, search: string, fromDate?: string | null, toDate?: string | null, region?: string | null): Observable<number[]> {
        let params = new HttpParams();
        if (companyId) params = params.set('companyId', companyId);
        if (search) params = params.set('search', search);
        if (fromDate) params = params.set('fromDate', fromDate);
        if (toDate) params = params.set('toDate', toDate);
        if (region) params = params.set('region', region);
        return this.http.get<number[]>(`${this.base}/salesregister/ids`, { params });
    }

    // distinct regions (filter dropdown)
    getSalesRegions(): Observable<string[]> {
        return this.http.get<string[]>(`${this.base}/salesregister/regions`);
    }

    // upload header validation ki list (backend single source of truth)
    getSalesTemplateHeaders(): Observable<string[]> {
        return this.http.get<string[]>(`${this.base}/salesregister/template-headers`);
    }

    downloadSalesTemplate(): Observable<Blob> {
        return this.http.get(`${this.base}/salesregister/template`, { responseType: 'blob' });
    }

    downloadDeliveryNoteTemplate(): Observable<Blob> {
        return this.http.get(`${this.base}/deliverynote/template`, { responseType: 'blob' });
    }

    updateSales(id: number, dto: SalesRegister): Observable<any> {
        return this.http.put(`${this.base}/salesregister/${id}`, dto);
    }

    deleteSales(id: number): Observable<any> {
        return this.http.delete(`${this.base}/salesregister/${id}`);
    }

    bulkDeleteSales(ids: number[]): Observable<any> {
        return this.http.post(`${this.base}/salesregister/bulk-delete`, ids);
    }

    // ---- Suppliers ----
    getSuppliers(companyId?: number | null): Observable<Supplier[]> {
        let params = new HttpParams().set('all', 'true');
        if (companyId) params = params.set('companyId', companyId);
        return this.http.get<Supplier[]>(`${this.base}/suppliers`, { params });
    }

    createSupplier(s: Supplier): Observable<any> {
        return this.http.post(`${this.base}/suppliers`, s);
    }

    updateSupplier(id: number, s: Supplier): Observable<any> {
        return this.http.put(`${this.base}/suppliers/${id}`, s);
    }

    deleteSupplier(id: number): Observable<any> {
        return this.http.delete(`${this.base}/suppliers/${id}`);
    }

    activateSupplier(id: number): Observable<any> {
        return this.http.put(`${this.base}/suppliers/${id}/activate`, {});
    }

    // ---- Delivery Note ----
    uploadDeliveryNote(companyId: number, file: File): Observable<any> {
        const form = new FormData();
        form.append('file', file);
        return this.http.post(`${this.base}/deliverynote/upload?companyId=${companyId}`, form);
    }

    // getDeliveryNoteList(companyId: number | null, page: number, pageSize: number, search: string): Observable<DeliveryNotePagedResult> {
    //     let params = new HttpParams().set('page', page).set('pageSize', pageSize);
    //     if (companyId) params = params.set('companyId', companyId);
    //     if (search) params = params.set('search', search);
    //     return this.http.get<DeliveryNotePagedResult>(`${this.base}/deliverynote`, { params });
    // }

    getDeliveryNoteList(companyId: number | null, page: number, pageSize: number, search: string, fromDate?: string | null, toDate?: string | null): Observable<DeliveryNotePagedResult> {
        let params = new HttpParams().set('page', page).set('pageSize', pageSize);
        if (companyId) params = params.set('companyId', companyId);
        if (search) params = params.set('search', search);
        if (fromDate) params = params.set('fromDate', fromDate);
        if (toDate) params = params.set('toDate', toDate);
        return this.http.get<DeliveryNotePagedResult>(`${this.base}/deliverynote`, { params });
    }

    // getDeliveryNoteIds(companyId: number | null, search: string): Observable<number[]> {
    //     let params = new HttpParams();
    //     if (companyId) params = params.set('companyId', companyId);
    //     if (search) params = params.set('search', search);
    //     return this.http.get<number[]>(`${this.base}/deliverynote/ids`, { params });
    // }

    getDeliveryNoteIds(companyId: number | null, search: string, fromDate?: string | null, toDate?: string | null): Observable<number[]> {
        let params = new HttpParams();
        if (companyId) params = params.set('companyId', companyId);
        if (search) params = params.set('search', search);
        if (fromDate) params = params.set('fromDate', fromDate);
        if (toDate) params = params.set('toDate', toDate);
        return this.http.get<number[]>(`${this.base}/deliverynote/ids`, { params });
    }

    updateDeliveryNote(id: number, dto: DeliveryNote): Observable<any> {
        return this.http.put(`${this.base}/deliverynote/${id}`, dto);
    }

    deleteDeliveryNote(id: number): Observable<any> {
        return this.http.delete(`${this.base}/deliverynote/${id}`);
    }

    bulkDeleteDeliveryNote(ids: number[]): Observable<any> {
        return this.http.post(`${this.base}/deliverynote/bulk-delete`, ids);
    }
}
